import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SupabaseService } from '../../services/supabase/supabase.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FileService } from '../../services/file/file-service';
import { NgIf } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppService } from '../../services/app/app.service';

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    NgIf,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  public readonly appName = inject(AppService).appName;
  private readonly supabase = inject(SupabaseService);
  private fileService = inject(FileService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private _snackBar = inject(MatSnackBar);
  signInForm = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      this.emailOrPhoneValidator(),
    ]),
  });

  otpFormGroup = new FormGroup({
    otp: new FormControl('', [Validators.required, Validators.minLength(6)]),
  });

  imageSrc = '';
  requiresOtp = false;
  redirectUrl: string | null =
    this.route.snapshot.queryParamMap.get('redirectUrl');
  errorMsg = 'There where an error during login, please try again.';

  async onSubmit(): Promise<void> {
    try {
      const email = this.signInForm.value.email as string;
      const loginUrl = `${location.origin}${this.redirectUrl}`;
      const response = this.isEmail(email)
        ? await this.supabase.signIn(email, loginUrl)
        : await this.supabase.signInViaPhone(email);

      if (response.error) {
        this._snackBar.open(this.errorMsg, 'Ok');
        return;
      }

      this.requiresOtp = !this.isEmail(email);

      if (!this.requiresOtp) {
        this._snackBar.open('Check your email for the login link!', 'Ok');
      }
    } catch (error) {
      this.alertCritical(error);
    } finally {
      //this.signInForm.reset()
    }
  }

  async onSubmitOtp(): Promise<void> {
    try {
      const phone = this.signInForm.value.email as string;
      const token = this.otpFormGroup.value.otp as string;
      const response = await this.supabase.verifyOtp(phone, token);
      if (response.error) {
        this._snackBar.open(this.errorMsg, 'Ok');
      } else {
        this.performRedirect();
      }
    } catch (error) {
      this.alertCritical(error);
    } finally {
      //this.signInForm.reset()
    }
  }

  async ngOnInit(): Promise<void> {
    void this.loadBackground();
    if ((await this.supabase.getSession()) != null) {
      this.performRedirect();
    }
  }

  private async loadBackground(): Promise<void> {
    const blob = await this.fileService.getFile(
      'bg_login',
      this.route.snapshot.paramMap.get('id') + '.jpg',
    );
    this.imageSrc = URL.createObjectURL(blob!);
  }

  private isEmail(value: string): boolean {
    return value.includes('@');
  }

  private performRedirect(): void {
    void this.router.navigate([
      this.redirectUrl
        ? this.redirectUrl
        : `/${this.route.snapshot.paramMap.get('id')}/home`,
    ]);
  }

  private emailOrPhoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      // Regular expression for email validation
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      // Regular expression for phone number validation
      // See: https://www.twilio.com/docs/glossary/what-e164#regex-matching-for-e164
      const phoneRegex = /^\+[1-9]\d{1,14}$/;

      // Check if the value matches email or phone regex
      const isValidEmail = emailRegex.test(value);
      const isValidPhone = phoneRegex.test(value);

      // If value is empty, or valid email or valid phone, validation passes
      if (!value || isValidEmail || isValidPhone) {
        return null; // No error
      }

      // Otherwise, return validation error
      return { emailOrPhone: true };
    };
  }

  private alertCritical(error: unknown): void {
    console.error(error);
    this._snackBar.open(this.errorMsg, 'Ok');
  }
}
